import React, {useEffect, useRef} from 'react';
import {Stack, styled} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import {
  contentLabActions,
  DraftRephrasingProject,
} from '@contentlab/stores/contentLabStore';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import {SrtItem} from '@contentlab/utils/srtUtils';
import SrtRow from '../srt-row';

const Container = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  spacing: '36px',
  background: designSystemToken('semantic.bg.secondary'),
  borderRadius: '8px',
  overflow: 'auto',
  gap: '28px',
  paddingInlineEnd: '24px',
  ...scrollbarMixins.overrideScrollbarHover(),
}));

function RephraseEditor({
  projectId,
  draftRephrasingProject,
  currentIndex,
  setCurrentIndex,
  onChange,
}: {
  projectId: string;
  draftRephrasingProject: DraftRephrasingProject;
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
  onChange?: (rowId: string, text: string) => void;
}) {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set the current index to the first item in the project if not already set
    const currentIndexId = draftRephrasingProject.currentSrt?.[0]?.id;

    if (currentIndexId && !currentIndex) {
      setCurrentIndex(currentIndexId);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element | null;

      const parentWithId = target?.closest('#rephrase-video-display');

      if (
        editorRef.current &&
        !editorRef.current.contains(target) &&
        !parentWithId
      ) {
        setCurrentIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setCurrentIndex]);

  return (
    <Container ref={editorRef}>
      <Stack gap="24px">
        {draftRephrasingProject.currentSrt.map((item: SrtItem) => {
          return (
            <SrtRow
              key={item.id}
              projectId={projectId}
              item={item}
              isEditing={item.id === currentIndex}
              isEdited={item.currentText !== item.originalText}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              editRow={(newText) => {
                contentLabActions.updateDraftRephrasingProjectSrt(
                  draftRephrasingProject.currentSrt.map((row: SrtItem) =>
                    row.id === currentIndex
                      ? {...row, currentText: newText}
                      : row
                  )
                );
                onChange?.(currentIndex as string, newText);
              }}
            />
          );
        })}
      </Stack>
    </Container>
  );
}

export default RephraseEditor;
